<template>
  <div>
    <Pie :data="chartData" :options="chartOptions" />
  </div>
</template>

<script setup>
import { Pie } from 'vue-chartjs'
import { Chart as ChartJS, Title, Tooltip, Legend, ArcElement } from 'chart.js'
import { computed } from 'vue'
import ChartDataLabels from 'chartjs-plugin-datalabels'

ChartJS.register(Title, Tooltip, Legend, ArcElement, ChartDataLabels)

const props = defineProps({
  labels: {
    type: Array,
    required: true
  },
  data: {
    type: Array,
    required: true
  },
  backgroundColors: {
    type: Array,
    required: false,
    default: () => [
      '#00423D', '#6DB3A0', '#BFD8B8', '#E6E6E6', '#A7C4BC', '#2E8B57'
    ]
  },
  title: {
    type: String,
    required: false,
    default: ''
  },
  options: {
    type: Object,
    required: false,
    default: () => ({})
  }
})

const chartData = computed(() => {
  // Calculate total for percentage
  const total = props.data.reduce((sum, value) => sum + value, 0);
  
  // Calculate percentages
  const percentageData = props.data.map(value => {
    const percentage = total > 0 ? Math.round((value / total) * 100) : 0;
    return percentage;
  });

  return {
    labels: props.labels,
    datasets: [
      {
        data: percentageData,
        backgroundColor: props.backgroundColors,
        borderWidth: 1
      }
    ]
  }
})

function deepMerge(target, source) {
  for (const key in source) {
    if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
      if (!target[key]) target[key] = {};
      deepMerge(target[key], source[key]);
    } else {
      target[key] = source[key];
    }
  }
  return target;
}

const defaultOptions = {
  responsive: true,
  plugins: {
    legend: {
      position: 'bottom',
      labels: {
        color: '#414141',
        font: { size: 14 }
      }
    },
    title: {
      display: !!props.title,
      text: props.title,
      color: '#00423D',
      font: { size: 18, weight: 'bold' }
    },
    tooltip: {
      callbacks: {
        label: function(context) {
          const percentage = context.raw;
          return `${context.label}: ${percentage} %`;
        }
      }
    },
    datalabels: {
      display: false
    }
  }
};

const chartOptions = computed(() => {
  // Merge defaultOptions with props.options (props.options takes precedence)
  return deepMerge(JSON.parse(JSON.stringify(defaultOptions)), props.options);
});
</script> 